import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Slider,
  Card,
  CardContent,
  Container,
  Grid,
  Alert
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  AutoAwesome as MagicIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon
} from "@mui/icons-material";
import LoadingScreen from "./components/LoadingScreen";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { trackPageView, trackAppEvents } from "./utils/analytics";

const SimpleApp = () => {
  const [imageUrl, setImageUrl] = useState("");
  const [originalImage, setOriginalImage] = useState(null);
  const [pixelatedImage, setPixelatedImage] = useState(null);
  const [pixelWidth, setPixelWidth] = useState(32);
  const [autoDetectedWidth, setAutoDetectedWidth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);
  const [isAppLoading, setIsAppLoading] = useState(true);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  // Auto-detect optimal pixel width based on image dimensions
  const calculateOptimalWidth = (imageWidth, imageHeight) => {
    const maxDimension = Math.max(imageWidth, imageHeight);

    // Calculate optimal width based on image size
    if (maxDimension <= 100) return 8; // Very small images
    if (maxDimension <= 200) return 16; // Small images
    if (maxDimension <= 400) return 32; // Medium images
    if (maxDimension <= 800) return 48; // Large images
    if (maxDimension <= 1200) return 64; // Very large images
    return 80; // Huge images
  };

  // Set image and auto-detect width
  const setImageWithAutoWidth = (imageDataUrl) => {
    setOriginalImage(imageDataUrl);
    setPixelatedImage(null);

    // Auto-detect optimal width
    const img = new Image();
    img.onload = () => {
      const optimalWidth = calculateOptimalWidth(img.width, img.height);
      setAutoDetectedWidth(optimalWidth * 2);
      setPixelWidth(optimalWidth * 2);
    };
    img.src = imageDataUrl;
  };

  // Fetch image from URL (direct fetch, no backend needed)
  const fetchImage = async () => {
    if (!imageUrl.trim()) {
      setError("Please enter an image URL");
      trackAppEvents.imageError("Empty URL");
      return;
    }

    setLoading(true);
    setError("");
    trackAppEvents.imageUrlSubmit(imageUrl);

    try {
      // Direct fetch from URL
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const reader = new FileReader();

      reader.onload = (e) => {
        setImageWithAutoWidth(e.target.result);
        setLoading(false);
        trackAppEvents.imageProcessed("URL", "success");
      };

      reader.onerror = () => {
        setError("Failed to load image from URL");
        setLoading(false);
        trackAppEvents.imageError("Reader Error");
      };

      reader.readAsDataURL(blob);
    } catch (err) {
      setError("Failed to fetch image: " + err.message);
      setLoading(false);
      trackAppEvents.fetchError(err.message);
    }
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    trackAppEvents.imageFileUpload(file.type);

    const reader = new FileReader();
    reader.onload = (e) => {
      setImageWithAutoWidth(e.target.result);
      setError("");
      trackAppEvents.imageProcessed("File Upload", "success");
    };
    reader.readAsDataURL(file);
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith("image/")) {
        trackAppEvents.imageDragDrop();
        trackAppEvents.imageFileUpload(file.type);
        const reader = new FileReader();
        reader.onload = (e) => {
          setImageWithAutoWidth(e.target.result);
          setError("");
          trackAppEvents.imageProcessed("Drag Drop", "success");
        };
        reader.readAsDataURL(file);
      } else {
        setError("Please drop a valid image file");
        trackAppEvents.imageError("Invalid File Type");
      }
    }
  };

  // Pixelate image using canvas
  const pixelateImage = useCallback(() => {
    if (!originalImage) {
      setError("No image to pixelate");
      trackAppEvents.imageError("No Image");
      return;
    }

    trackAppEvents.pixelateClick();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      // Set canvas size
      canvas.width = img.width;
      canvas.height = img.height;

      // Draw original image
      ctx.drawImage(img, 0, 0);

      // Get image data
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Calculate pixel size
      const pixelSize = Math.max(1, Math.floor(img.width / pixelWidth));

      // Apply pixelation
      for (let y = 0; y < canvas.height; y += pixelSize) {
        for (let x = 0; x < canvas.width; x += pixelSize) {
          // Calculate average color for this block
          let r = 0,
            g = 0,
            b = 0,
            a = 0;
          let count = 0;

          for (let by = 0; by < pixelSize && y + by < canvas.height; by++) {
            for (let bx = 0; bx < pixelSize && x + bx < canvas.width; bx++) {
              const index = ((y + by) * canvas.width + (x + bx)) * 4;
              r += data[index];
              g += data[index + 1];
              b += data[index + 2];
              a += data[index + 3];
              count++;
            }
          }

          if (count > 0) {
            r = Math.round(r / count);
            g = Math.round(g / count);
            b = Math.round(b / count);
            a = Math.round(a / count);

            // Fill the block with the average color
            for (let by = 0; by < pixelSize && y + by < canvas.height; by++) {
              for (let bx = 0; bx < pixelSize && x + bx < canvas.width; bx++) {
                const index = ((y + by) * canvas.width + (x + bx)) * 4;
                data[index] = r;
                data[index + 1] = g;
                data[index + 2] = b;
                data[index + 3] = a;
              }
            }
          }
        }
      }

      // Put the modified image data back
      ctx.putImageData(imageData, 0, 0);

      // Convert to data URL
      const pixelatedDataUrl = canvas.toDataURL("image/png");
      setPixelatedImage(pixelatedDataUrl);
      trackAppEvents.imageProcessed(
        `Original: ${img.width}x${img.height}`,
        `Pixelated: ${pixelWidth}px`
      );
    };

    img.src = originalImage;
  }, [originalImage, pixelWidth]);

  // Download image
  const downloadImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!pixelatedImage) return;

    trackAppEvents.imageDownload();

    const link = document.createElement("a");
    link.download = "pixelated-image.png";
    link.href = pixelatedImage;
    link.click();
  };

  // Remove auto-pixelation - only pixelate when button is clicked

  // Handle app loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsAppLoading(false);
      trackAppEvents.appLoad();
    }, 2000); // Show loading for 2 seconds

    return () => clearTimeout(timer);
  }, []);

  // Track page view on component mount
  useEffect(() => {
    trackPageView("/app");
  }, []);

  // Show loading screen
  if (isAppLoading) {
    return <LoadingScreen />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)",
        color: "#ffffff",
        position: "relative"
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
        {/* Header */}
        <Header />

        {/* App Title */}
        <Box sx={{ textAlign: "center", mb: 4, py: 4 }}>
          <Typography
            variant="h4"
            sx={{
              color: "#00ff00",
              fontFamily: "monospace",
              fontWeight: "bold",
              fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" }
            }}
          >
            MEME PIXEL ART GENERATOR
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#888",
              mt: 1,
              fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" }
            }}
          >
            🔥 Transform ANY image into LEGENDARY pixel art! Perfect for memes &
            NFTs! 🚀
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Controls */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                background: "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)",
                border: "2px solid #00ff00"
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{ color: "#00ff00", mb: 3, textAlign: "center" }}
                >
                  Controls
                </Typography>

                {/* Image URL Input */}
                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    autoComplete="off"
                    label="Image URL"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        color: "#00ff00",
                        "& fieldset": { borderColor: "#00ff00" },
                        "&:hover fieldset": { borderColor: "#00ff00" },
                        "&.Mui-focused fieldset": { borderColor: "#00ff00" }
                      },
                      "& .MuiInputLabel-root": { color: "#888" },
                      "& .MuiInputLabel-root.Mui-focused": { color: "#00ff00" }
                    }}
                  />
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={fetchImage}
                    disabled={loading}
                    startIcon={<MagicIcon />}
                    sx={{
                      mt: 1,
                      background: "#00ff00",
                      color: "#000",
                      "&:hover": { background: "#00cc00" }
                    }}
                  >
                    {loading ? "Fetching..." : "Fetch Image"}
                  </Button>
                </Box>

                {/* File Upload */}
                <Box sx={{ mb: 3 }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    ref={fileInputRef}
                    style={{ display: "none" }}
                  />
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => fileInputRef.current?.click()}
                    startIcon={<UploadIcon />}
                    sx={{
                      borderColor: "#00ff00",
                      color: "#00ff00",
                      "&:hover": {
                        borderColor: "#00ff00",
                        background: "rgba(0, 255, 0, 0.1)"
                      }
                    }}
                  >
                    Upload Image
                  </Button>
                </Box>

                {/* Pixel Width Slider */}
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1
                    }}
                  >
                    <Typography sx={{ color: "#00ff00" }}>
                      Pixel Width: {pixelWidth}
                    </Typography>
                    {autoDetectedWidth && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setPixelWidth(autoDetectedWidth);
                          trackAppEvents.autoWidthClick(autoDetectedWidth);
                        }}
                        sx={{
                          borderColor: "#00ff00",
                          color: "#00ff00",
                          fontSize: "0.75rem",
                          py: 0.5,
                          px: 1,
                          "&:hover": {
                            borderColor: "#00ff00",
                            background: "rgba(0, 255, 0, 0.1)"
                          }
                        }}
                      >
                        Auto ({autoDetectedWidth})
                      </Button>
                    )}
                  </Box>
                  <Slider
                    value={pixelWidth}
                    onChange={(e, value) => {
                      setPixelWidth(value);
                      trackAppEvents.pixelWidthChange(value);
                    }}
                    min={8}
                    max={512}
                    step={8}
                    sx={{
                      color: "#00ff00",
                      "& .MuiSlider-thumb": { border: "2px solid #00ff00" }
                    }}
                  />
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: "flex", gap: 1, flexDirection: "column" }}>
                  <Button
                    variant="contained"
                    onClick={pixelateImage}
                    disabled={!originalImage}
                    startIcon={<RefreshIcon />}
                    sx={{
                      background: "#00ff00",
                      color: "#000",
                      "&:hover": { background: "#00cc00" }
                    }}
                  >
                    Pixelate
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Image Display */}
          <Grid item xs={12} md={8}>
            <Paper
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                p: 2,
                background: isDragOver
                  ? "linear-gradient(135deg, #0a2a0a 0%, #1a3a1a 100%)"
                  : "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)",
                border: isDragOver ? "2px dashed #00ff00" : "2px solid #00ff00",
                minHeight: "500px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #0a1a0a 0%, #1a2a1a 100%)"
                }
              }}
            >
              {originalImage ? (
                <Box sx={{ textAlign: "center" }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 2,
                      mb: 2
                    }}
                  >
                    <Typography variant="h6" sx={{ color: "#00ff00" }}>
                      {pixelatedImage ? "Pixelated Image" : "Original Image"}
                    </Typography>
                    {pixelatedImage && (
                      <Button
                        variant="outlined"
                        onClick={downloadImage}
                        startIcon={<DownloadIcon />}
                        size="small"
                        sx={{
                          borderColor: "#00ff00",
                          color: "#00ff00",
                          fontSize: "0.75rem",
                          py: 0.5,
                          px: 1,
                          minWidth: "auto",
                          "&:hover": {
                            borderColor: "#00ff00",
                            background: "rgba(0, 255, 0, 0.1)"
                          }
                        }}
                      >
                        Download
                      </Button>
                    )}
                  </Box>
                  <img
                    src={pixelatedImage || originalImage}
                    alt="Processed"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "400px",
                      border: "1px solid #00ff00",
                      imageRendering: pixelatedImage ? "pixelated" : "auto"
                    }}
                  />
                </Box>
              ) : (
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="h6" sx={{ color: "#888", mb: 2 }}>
                    {isDragOver
                      ? "Drop your image here!"
                      : "Click or drag & drop an image to get started"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    {isDragOver
                      ? "Release to upload"
                      : "Click to browse files, or drag & drop an image here"}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Hidden canvas for pixelation */}
        <canvas ref={canvasRef} style={{ display: "none" }} />

        {/* Footer */}
        <Footer />
      </Container>
    </Box>
  );
};

export default SimpleApp;
